/**
 * GameScene — the side-scrolling battle view shown inside the PiP.
 *
 * It does NOT compute combat. The core game loop resolves rounds and emits
 * `combat:*` events; this scene reflects them. The hero marches in place (walk
 * cycle) while the scenery scrolls behind him, swings on each attack, monsters
 * slide in from the right. Everyone is grounded on the grass with a shadow.
 */

import { createAnimatedCharacter, createMonster } from './sprites.js';
import { bus } from '../core/events.js';
import { bossKillThreshold } from '../core/combat.js';
import { xpProgress } from '../core/progression.js';
import { lockRemainingMs, formatLockTime } from '../core/inventory.js';

/** Map a monster id to its loaded texture key. */
const MOB_TEXTURE = {
  rat: 'mob_rat',
  bird: 'mob_bird',
  wolf: 'mob_wolf',
  alpha_wolf: 'mob_alpha_wolf',
  spider: 'mob_spider',
  bandit: 'mob_bandit',
  treant: 'mob_treant',
  elder_treant: 'mob_elder_treant',
  storm_harpy: 'mob_storm_harpy',
  astral_golem: 'mob_astral_golem',
  seraph_sentinel: 'mob_seraph_sentinel',
  celestial_archon: 'mob_celestial_archon',
  nebula_wraith: 'mob_nebula_wraith',
  astral_serpent: 'mob_astral_serpent',
  cosmic_devourer: 'mob_cosmic_devourer',
  star_titan: 'mob_star_titan',
  bone_knight: 'mob_bone_knight',
  plague_lich: 'mob_plague_lich',
  wraith_king: 'mob_wraith_king',
  death_sovereign: 'mob_death_sovereign',
  hellhound: 'mob_hellhound',
  flame_demon: 'mob_flame_demon',
  dread_knight: 'mob_dread_knight',
  demon_lord: 'mob_demon_lord',
  crystal_shardling: 'mob_crystal_shardling',
  prism_stalker: 'mob_prism_stalker',
  geode_brute: 'mob_geode_brute',
  crystal_monarch: 'mob_crystal_monarch',
  sand_wraith: 'mob_sand_wraith',
  bone_scorpion: 'mob_bone_scorpion',
  tomb_guardian: 'mob_tomb_guardian',
  cursed_pharaoh: 'mob_cursed_pharaoh',
  bog_hag: 'mob_bog_hag',
  plague_toad: 'mob_plague_toad',
  rot_behemoth: 'mob_rot_behemoth',
  swamp_horror: 'mob_swamp_horror',
  clockwork_soldier: 'mob_clockwork_soldier',
  brass_spider: 'mob_brass_spider',
  steam_golem: 'mob_steam_golem',
  mechanical_overlord: 'mob_mechanical_overlord',
};

/** Map a map id to its background texture key. */
const MAP_BG = {
  green_meadows: 'bg_meadow',
  dark_forest: 'bg_dark_forest',
  frozen_peaks: 'bg_frozen_peaks',
  lava_caves: 'bg_lava_caves',
  shadow_abyss: 'bg_shadow_abyss',
  drowned_depths: 'bg_drowned_depths',
  celestial_throne: 'bg_celestial_throne',
  vazio_estelar: 'bg_vazio_estelar',
  eternal_necropolis: 'bg_eternal_necropolis',
  crimson_inferno: 'bg_crimson_inferno',
  crystal_caverns: 'bg_crystal_caverns',
  cursed_desert: 'bg_cursed_desert',
  putrid_swamp: 'bg_putrid_swamp',
  mechanical_citadel: 'bg_mechanical_citadel',
};

/** Monsters that attack from range (don't close in) — everyone else is melee. */
const RANGED_MONSTERS = new Set(['spider', 'storm_harpy', 'nebula_wraith', 'cosmic_devourer', 'plague_lich', 'wraith_king', 'flame_demon', 'crystal_shardling', 'sand_wraith', 'bog_hag', 'brass_spider']);

/**
 * On-screen size multiplier per creature (x the 88px base). Without this every
 * monster renders the same height, so a rat stands eye-to-eye with a direwolf.
 * Small critters shrink, hulks grow a touch; bosses still get the x1.25 on top.
 */
const MOB_SIZE = {
  rat: 0.58, bird: 0.60, wolf: 0.95,
  spider: 0.72, bandit: 0.90, treant: 1.06,
  frost_imp: 0.68, ice_golem: 1.04, frost_wraith: 0.88,
  fire_elemental: 0.90, magma_golem: 1.06, lava_serpent: 0.95,
  shadow_fiend: 0.90, void_golem: 1.06, soul_reaper: 0.96,
  abyss_angler: 0.78, drowned_knight: 0.94, deep_lurker: 1.0,
  storm_harpy: 0.85, astral_golem: 1.06, seraph_sentinel: 0.95,
  nebula_wraith: 0.90, astral_serpent: 1.0, cosmic_devourer: 1.06,
  bone_knight: 0.92, plague_lich: 0.95, wraith_king: 1.0,
  hellhound: 0.85, flame_demon: 1.02, dread_knight: 0.95,
  crystal_shardling: 0.62, prism_stalker: 0.88, geode_brute: 1.08,
  sand_wraith: 0.90, bone_scorpion: 0.78, tomb_guardian: 1.02,
  bog_hag: 0.85, plague_toad: 0.78, rot_behemoth: 1.10,
  clockwork_soldier: 0.90, brass_spider: 0.72, steam_golem: 1.08,
};

/** Glyph shown in a drop balloon for each food type. */
const FOOD_GLYPH = { apple: '🍎', strawberry: '🍓', meat: '🍖' };

/** Per-class glyph shown beside each hero's HP bar so you know whose it is. */
const CLASS_ICON = { knight: '🛡️', rogue: '🗡️', ranger: '🏹', pyromancer: '🔥', priest: '✝️' };

/** Compact HP number so big pools still fit the tiny bar (e.g. 12.3k, 1.2M). */
function fmtHp(n) {
  n = Math.max(0, Math.round(n));
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1e4) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
}

/**
 * How each class lands a hit: melee swings connect almost instantly, ranged and
 * caster classes throw a projectile that visibly travels to the monster — damage
 * only lands when the effect reaches it. `beat` is how long the renderer waits on
 * this hit before playing the next event (death), so the projectile always lands
 * before the monster dies.
 */
const CLASS_ATTACK = {
  // Melee beats cover the full dash-in (150+windup) plus the strike, so a kill
  // never plays the death anim while the hero is still mid-charge.
  knight:     { kind: 'melee',    color: 0xffffff, windup: 110, beat: 540, engageGap: 72 },
  rogue:      { kind: 'melee',    color: 0x9fe8ff, windup: 90,  beat: 500, engageGap: 66 },
  ranger:     { kind: 'arrow',    color: 0xe8d2a0, travel: 300, beat: 420, engageGap: 120 },
  pyromancer: { kind: 'fireball', color: 0xff7a2a, travel: 360, beat: 480, engageGap: 130 },
  priest:     { kind: 'bolt',     color: 0xffe49a, travel: 320, beat: 440, engageGap: 125 },
};

/**
 * Visual for a ranged monster's strike — a small orb that travels from the
 * monster to the hero, so the hit reads as a real attack instead of just a
 * self-contained wobble (`RANGED_MONSTERS` previously had no travelling FX at
 * all, which read as "no attack animation"). Per-monster color/size; anything
 * ranged without an entry here falls back to `default`.
 */
const MONSTER_RANGED_FX = {
  spider:       { color: 0x8fe000, glow: 0x3c6b00, travel: 260 }, // venom spit
  storm_harpy:  { color: 0xbfe9ff, glow: 0x4d9fd6, travel: 240 },
  brass_spider: { color: 0xffcf5c, glow: 0x8a6a1e, travel: 260 },
  default:      { color: 0x9d6bd6, glow: 0x4a2a70, travel: 260 },
};

export class GameScene extends Phaser.Scene {
  constructor() {
    super('Game');
    this.state = null;
    this.data = null;
    this.heroes = [];   // animated characters, one per active party hero
    this.monsters = [];  // live monster sprites in the current pack
  }

  /** @param {{state:object, data:object}} d */
  init(d) {
    this.state = d.state;
    this.data = d.data;
  }

  create() {
    const { width, height } = this.scale;
    this.groundY = height * 0.92; // grass baseline characters stand on
    this.homeX = width * 0.20;    // resting/start point the front hero returns to
    this.engageX = width * 0.46;  // forward fighting line
    this.mobX = width * 0.72;     // front-monster slot
    this.monsters = [];
    this.heroes = [];

    // Scrolling scenario — pick texture based on active map.
    const bgKey = MAP_BG[this.state.activeMapId] ?? 'bg_meadow';
    const resolvedBg = this.textures.exists(bgKey) ? bgKey : 'bg_meadow';
    this.bg = this.add.tileSprite(0, 0, width, height, resolvedBg).setOrigin(0, 0);
    const tex = this.textures.get(resolvedBg).getSourceImage();
    // Use "cover" scaling so the bg always fills the canvas with no black bars.
    const s = Math.max(width / tex.width, height / tex.height);
    this.bg.tileScaleX = s;
    this.bg.tileScaleY = s;

    // Map name, bottom-center — below the ground line the heroes walk on, so it
    // doesn't compete with the HP/level/gold readouts crowded along the top.
    // Swaps to a "MASMORRA" banner while a daily dungeon run is active (kept
    // live in _refreshHud since dungeon mode doesn't restart the scene).
    this._baseMapLabel = this.data?.maps?.[this.state.activeMapId]?.label ?? '';
    this.mapLabelText = this.add
      .text(width / 2, height - 3, this._baseMapLabel, {
        fontFamily: 'monospace', fontSize: '10px', color: '#f3ead6',
        stroke: '#1a1620', strokeThickness: 3,
      })
      .setOrigin(0.5, 1)
      .setDepth(30);

    // Heroes: one per active party member. The front hero rests at homeX; a
    // second hero stands slightly behind and above for depth.
    this._buildHeroes();

    this._buildHud(width);

    // The combat loop resolves a whole round (spawn → hit → kill → drops) inside
    // ONE synchronous event burst. Playing those directly makes every monster
    // spawn and die in the same frame — it never visibly slides in. So we don't
    // act on events directly: we queue them and a pump in update() plays them
    // back with a minimum beat between each, giving every monster real presence.
    this._queue = [];
    this._beatUntil = 0;
    const enq = (type) => (e) => this._queue.push({ type, e });
    this._subs = [
      bus.on('combat:spawn', enq('spawn')),
      bus.on('combat:hero_attack', enq('hero_attack')),
      bus.on('combat:monster_attack', enq('monster_attack')),
      bus.on('combat:hero_dodge', enq('dodge')),
      bus.on('combat:lifesteal', enq('lifesteal')),
      bus.on('combat:aura_heal', enq('aura_heal')),
      bus.on('combat:thorns', enq('thorns')),
      bus.on('combat:poison_tick', enq('poison_tick')),
      bus.on('combat:hero_poison_tick', enq('hero_poison_tick')),
      bus.on('combat:monster_lifesteal', enq('monster_lifesteal')),
      bus.on('combat:kill', enq('kill')),
      bus.on('combat:hero_down', enq('hero_down')),
      bus.on('combat:hero_revive', enq('hero_revive')),
      bus.on('combat:hero_revived_kit', enq('hero_revive')),
      bus.on('combat:food_eat', enq('food')),
      bus.on('combat:drop_equipment', enq('drop_eq')),
      bus.on('combat:drop_food', enq('drop_food')),
      bus.on('combat:drop_revive', enq('drop_revive')),
      bus.on('combat:drop_protection', enq('drop_protection')),
      bus.on('combat:dungeon_clear', enq('dungeon_clear')),
      bus.on('combat:dungeon_fail', enq('dungeon_fail')),
      bus.on('combat:level_up', enq('level_up')),
      // While ANY management panel is open it covers the game, so canvas clicks
      // (hero sprites, HUD bars, backpack icon) must not fire — otherwise a
      // click landing just outside the panel (e.g. on the hero-selection page)
      // navigates away instead of doing nothing. Toggle canvas input with it.
      bus.on('panel:opened', () => { this.input.enabled = false; }),
      bus.on('panel:closed', () => { this.input.enabled = true; }),
    ];
    this.events.once('shutdown', () => this._subs.forEach((off) => off()));

    if (this.state.currentMonsters?.length) this._spawnPack(this.state.currentMonsters);
  }

  /** Build the hero sprites from the active party. */
  _buildHeroes() {
    const party = this.state?.party ?? [];
    // Same target height for every hero so a duo never reads as "one bigger than
    // the other"; classes differ only in their own art proportions.
    const tall = party.length > 1 ? 96 : 104;
    // Visual placement order: melee front-liners (knight/rogue) stand closest to
    // the monsters, casters/archers behind — matching who actually tanks. The
    // hero ARRAY index (heroIndex in events) is untouched; only the on-screen
    // slot changes, so combat/HUD wiring stays aligned.
    const order = party.map((_h, i) => i).sort((a, b) => {
      const fa = this.data.classes[party[a].classId]?.frontline ? 0 : 1;
      const fb = this.data.classes[party[b].classId]?.frontline ? 0 : 1;
      return fa - fb || a - b;
    });
    const slotOf = {};
    order.forEach((heroIdx, slot) => { slotOf[heroIdx] = slot; });
    party.forEach((h, i) => {
      const classId = h.classId ?? 'knight';
      // Both heroes stand on the SAME ground line (equal apparent size); the
      // back hero is only staggered horizontally + drawn behind for depth.
      const slot = slotOf[i] ?? i;
      const homeX = this.homeX - slot * 34;
      const homeY = this.groundY;
      const char = createAnimatedCharacter(this, homeX, homeY, {
        id: classId,
        walk: [0, 1, 2, 3].map((k) => `${classId}_walk_${k}`),
        attack: [0, 1, 2, 3].map((k) => `${classId}_attack_${k}`),
        targetHeight: tall,
      });
      char.container.setDepth(6 - slot); // front-slot hero drawn on top
      // Desync the walk cycle so a duo doesn't march in robotic lockstep.
      char.sprite.anims?.setProgress?.((i * 0.37) % 1);
      if (!h.alive) char.container.setAlpha(0.35);

      // Click a hero to open the HERÓI panel focused on THAT hero (manage it).
      // Use the sprite's full-frame rectangle as the hit area (NOT pixel-perfect):
      // on the tiny, scaled-up PiP canvas pixel-perfect testing made the hero
      // almost impossible to click — taps landing on the transparent margin (or
      // a hair off an opaque pixel) were silently ignored. The forgiving
      // rectangle just works; when two heroes overlap, the front hero's higher
      // depth means it wins the click, and each HP bar is a reliable per-hero
      // selector anyway.
      char.sprite.setInteractive({ useHandCursor: true });
      char.sprite.on('pointerover', () => this.input.setDefaultCursor('pointer'));
      char.sprite.on('pointerout', () => this.input.setDefaultCursor('default'));
      char.sprite.on('pointerdown', () => { this.input.setDefaultCursor('default'); bus.emit('ui:openHero', { heroIndex: i }); });

      this.heroes.push({ char, classId, homeX, homeY });
    });
  }

  update(_t, dt) {
    this._pumpQueue();
    // March (fast scroll) only between fights; slow the scroll while paced events
    // are still playing out so the scene reads as "engaged".
    const marching = !this._anyMonsterAlive() && this._queue.length === 0;
    this.bg.tilePositionX += (marching ? 0.04 : 0.012) * dt;
    if (marching) {
      this.heroes.forEach((hero, i) => { if (this.state.party[i]?.alive) hero.char.walk(); });
    }
    this._refreshHud();
    this._followBalloons();
  }

  /** Keep each drop card hovering over the hero that earned it as they move. */
  _followBalloons() {
    if (!this._balloons?.length) return;
    for (const c of this._balloons) {
      const hi = c.getData('heroIndex');
      if (hi == null) continue;
      const w = c.getData('cardW') ?? 64;
      const target = Phaser.Math.Clamp(this._heroX(hi), w / 2 + 4, this.scale.width - w / 2 - 4);
      c.x += (target - c.x) * 0.2; // ease toward the hero so it tracks smoothly
    }
  }

  /** Whether any monster in the current pack is still standing. */
  _anyMonsterAlive() {
    return this.monsters.some((m) => m && m.alive);
  }

  // --- Paced event playback -------------------------------------------------
  /** Play queued combat events with a minimum beat between each. */
  _pumpQueue() {
    if (!this._queue.length) return;
    // If we fell behind (tab was backgrounded/throttled so update() barely ran),
    // drop the stale backlog and resume paced playback from the most recent
    // events — combat logic already applied, this is purely cosmetic. Trimming
    // (instead of dumping the whole queue at once) keeps the swing/projectile
    // effects visible rather than collapsing them into one silent burst.
    if (this._queue.length > 24) {
      this._queue = this._queue.slice(-8);
      this._beatUntil = 0;
    }
    const now = this.time.now;
    while (this._queue.length && now >= this._beatUntil) {
      const { type, e } = this._queue.shift();
      this._beatUntil = now + this._playEvent(type, e);
    }
  }

  /** Execute one combat event; returns the ms to wait before the next beat. */
  _playEvent(type, e) {
    switch (type) {
      case 'spawn': this._spawnPack(e.monsters); return 340;
      case 'hero_attack': this._onHeroAttack(e); return this._attackVisual(e.heroIndex).beat;
      case 'monster_attack': {
        this._onMonsterAttack(e);
        const mob = this.monsters[e.monsterIndex];
        return (mob && !mob.ranged) ? 440 : 220;
      }
      case 'dodge': this._floatText(this._heroX(e.heroIndex), this.groundY - 80, 'esquiva', '#9ad1ff'); return 160;
      case 'lifesteal': this._floatText(this._heroX(e.heroIndex), this.groundY - 96, `+${e.heal} ❤`, '#7CFC4D'); return 0;
      case 'aura_heal': this._floatText(this._heroX(e.heroIndex), this.groundY - 104, `+${e.heal} ✨`, '#bdf0c0'); return 0;
      case 'thorns': {
        const tm = this.monsters[e.monsterIndex];
        if (tm) this._floatText(tm.container.x, this.groundY - 96, `⟲${e.damage}`, '#ffb060');
        return 0;
      }
      case 'poison_tick': {
        const pm = this.monsters[e.monsterIndex];
        if (pm) this._floatText(pm.container.x, this.groundY - 88, `☠${e.damage}`, '#8ee06a');
        return 0;
      }
      case 'hero_poison_tick': this._floatText(this._heroX(e.heroIndex), this.groundY - 88, `☠${e.damage}`, '#b6f06a'); return 0;
      case 'monster_lifesteal': {
        const lm = this.monsters[e.monsterIndex];
        if (lm) this._floatText(lm.container.x, this.groundY - 104, `+${e.heal} ❤`, '#ff6b8a');
        return 0;
      }
      case 'kill': this._onKill(e); return 360;
      case 'hero_down': this._onHeroDown(e); return 160;
      case 'hero_revive': this._onHeroRevive(e); return 120;
      case 'food': this._floatText(this._heroX(e.heroIndex), this.groundY - 78, `+${e.heal} ❤`, '#46d160'); return 0;
      case 'drop_eq': this._dropBalloon(e.heroIndex, { glyph: '🎁', label: e.item.label, color: this.data.rarity?.[e.item.rarity]?.color ?? '#ffffff' }); return 0;
      case 'drop_food': this._dropBalloon(e.heroIndex, { glyph: FOOD_GLYPH[e.food.foodId] ?? '🍎', label: e.food.label, color: '#46d160' }); return 0;
      case 'drop_revive': this._dropBalloon(e.heroIndex, { glyph: '⛑️', label: 'Kit de Reviver', color: '#ff9a3c' }); return 0;
      case 'drop_protection': this._dropBalloon(e.heroIndex, { glyph: '🛡️', label: 'Amuleto de Proteção', color: '#a335ee' }); return 0;
      case 'dungeon_clear': this._floatText(this._heroX(e.heroIndex), this.groundY - 100, 'MASMORRA CONCLUÍDA!', '#ffd34d', true); this._shake(280, 0.01, true); return 0;
      case 'dungeon_fail': this._floatText(this._heroX(e.heroIndex), this.groundY - 100, 'masmorra falhou...', '#ff6b6b', true); return 0;
      case 'level_up': this._onLevelUp(e); return 0;
      default: return 0;
    }
  }

  /** Current screen x of a hero by index (falls back to its home anchor). */
  _heroX(i) {
    return this.heroes[i]?.char.container.x ?? this.heroes[i]?.homeX ?? this.homeX;
  }

  // --- HUD ------------------------------------------------------------------
  // One HP/XP/level block per active hero, stacked top-left; gold top-right.
  _buildHud(width) {
    this.hpBarW = 62;
    this.hud = [];
    const party = this.state?.party ?? [];
    const barX = 24; // leave room on the left for a per-class glyph
    party.forEach((hero, i) => {
      const y = 8 + i * 24;
      // Class emblem so you can tell at a glance which hero's bar is which.
      // Prefer the generated icon; fall back to a glyph if it didn't load.
      const iconKey = `class_${hero?.classId}`;
      if (this.textures.exists(iconKey)) {
        const emblem = this.add.image(14, y + 7, iconKey).setDepth(41);
        emblem.setDisplaySize(16, 16 * (emblem.height / emblem.width));
      } else {
        this.add.text(7, y + 6, CLASS_ICON[hero?.classId] ?? '•', {
          fontFamily: 'sans-serif', fontSize: '13px', stroke: '#1a1620', strokeThickness: 3,
        }).setOrigin(0, 0.5).setDepth(41);
      }
      this.add.rectangle(barX, y, this.hpBarW, 8, 0x000000, 0.55).setOrigin(0, 0).setStrokeStyle(1, 0x3a3147);
      const hpFill = this.add.rectangle(barX + 1, y + 1, this.hpBarW - 2, 6, 0xe14b4b).setOrigin(0, 0);
      this.add.rectangle(barX, y + 10, this.hpBarW, 4, 0x000000, 0.55).setOrigin(0, 0).setStrokeStyle(1, 0x3a3147);
      const xpFill = this.add.rectangle(barX + 1, y + 11, this.hpBarW - 2, 2, 0x46a0ff).setOrigin(0, 0);
      // Current/max HP printed over the bar so you can read exact health.
      const hpText = this.add
        .text(barX + this.hpBarW / 2, y + 4, '', {
          fontFamily: 'monospace', fontSize: '8px', color: '#ffffff', stroke: '#1a1620', strokeThickness: 2,
        })
        .setOrigin(0.5, 0.5).setDepth(41);
      const levelText = this.add
        .text(barX + this.hpBarW + 6, y, '', {
          fontFamily: 'monospace', fontSize: '10px', color: '#ffd98a', stroke: '#1a1620', strokeThickness: 3,
        })
        .setOrigin(0, 0);
      // An invisible hit area over the whole HUD block — a reliable, unambiguous
      // way to open THIS hero's HERÓI panel (the canvas sprites overlap, so
      // clicking them is fiddly when two heroes are close together).
      const hit = this.add.rectangle(6, y - 2, barX + this.hpBarW + 56, 20, 0xffffff, 0.001)
        .setOrigin(0, 0).setDepth(40).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => bus.emit('ui:openHero', { heroIndex: i }));
      this.hud.push({ hpFill, xpFill, levelText, hpText });
    });
    this.goldText = this.add
      .text(width - 8, 8, '', {
        fontFamily: 'monospace', fontSize: '11px', color: '#e8b454',
        stroke: '#1a1620', strokeThickness: 3,
      })
      .setOrigin(1, 0);

    // Ambient "kills until boss" countdown so the player doesn't have to open
    // the PORTAL panel just to see how close the next boss fight is.
    this.bossCountText = this.add
      .text(width - 8, 20, '', {
        fontFamily: 'monospace', fontSize: '9px', color: '#e8a054',
        stroke: '#1a1620', strokeThickness: 3,
      })
      .setOrigin(1, 0);
  }

  _refreshHud() {
    const party = this.state?.party ?? [];
    const cfg = this.data?.balance?.progression;
    party.forEach((hero, i) => {
      const h = this.hud[i];
      if (!h) return;
      if (hero.alive) {
        const pct = Phaser.Math.Clamp(hero.maxHp ? hero.hp / hero.maxHp : 0, 0, 1);
        h.hpFill.setAlpha(1).scaleX = pct;
        if (cfg) h.xpFill.scaleX = xpProgress(hero, cfg).pct;
        h.levelText.setText(`Nv.${hero.level}`).setColor('#ffd98a');
        if (h.hpText) h.hpText.setText(`${fmtHp(hero.hp)}/${fmtHp(hero.maxHp)}`).setVisible(true);
      } else {
        // Locked-out / downed hero: empty HP bar, with the countdown shown
        // where the HP number normally sits — big and red, since a small
        // badge tucked next to the level number was too easy to miss.
        h.hpFill.scaleX = 0;
        const lockMs = lockRemainingMs(hero);
        if (h.hpText) {
          h.hpText.setText(lockMs > 0 ? `🔒 ${formatLockTime(lockMs)}` : '🔒 voltando...')
            .setColor('#ff8a7a').setVisible(true);
        }
        h.levelText.setText(`Nv.${hero.level}`).setColor('#9a8fb0');
      }
    });
    if (this.goldText) this.goldText.setText(`⛁ ${this.state.player.gold}`);
    if (this.bossCountText) {
      const map = this.data?.maps?.[this.state.activeMapId];
      const every = map?.boss ? bossKillThreshold(map) : 0;
      if (every && !this.state.dungeonRun) {
        const left = Math.max(0, every - (this.state.killsSinceBoss ?? 0));
        this.bossCountText.setText(`💀 ${left}`).setVisible(true);
      } else {
        this.bossCountText.setVisible(false);
      }
    }
    if (this.mapLabelText) {
      const inDungeon = !!this.state.dungeonRun;
      this.mapLabelText.setText(inDungeon ? '⚔ MASMORRA ⚔' : this._baseMapLabel);
      this.mapLabelText.setColor(inDungeon ? '#ff8000' : '#f3ead6');
    }
  }

  /** A backpack icon inside the PiP that opens the unified HERO window. */
  _buildMenuIcon(width) {
    const x = width - 18;
    const y = 30;
    const box = this.add.rectangle(x, y, 24, 24, 0x2c2118, 0.9).setStrokeStyle(1.5, 0xb8862f).setDepth(50);
    const ic = this.add.text(x, y, '🎒', { fontSize: '14px' }).setOrigin(0.5).setDepth(51);
    box.setInteractive({ useHandCursor: true });
    box.on('pointerover', () => box.setStrokeStyle(1.5, 0xffd98a));
    box.on('pointerout', () => box.setStrokeStyle(1.5, 0xb8862f));
    box.on('pointerdown', () => bus.emit('ui:openHero'));
    ic.setInteractive({ useHandCursor: true });
    ic.on('pointerdown', () => bus.emit('ui:openHero'));
  }

  // --- Monster lifecycle ----------------------------------------------------
  /** Spawn a whole pack (1–3 monsters), staggered to the right, and advance heroes. */
  _spawnPack(monsters) {
    // Sweep any lingering monster containers. At high combat speed monsters die
    // faster (~40ms) than the 240ms death animation, so several death tweens can
    // overlap and pile garbled sprites on screen — force-clear them first.
    this._sweepMonsters();
    // Effective monster level for the badge: the zone floor, raised toward the
    // zone cap as the party levels (mirrors zoneScaleMult in core/combat.js).
    const map = this.data.maps[this.state.activeMapId];
    const zs = this.data.balance?.zoneScaling;
    let mobLevel = map?.levelRange?.[0] ?? 1;
    if (map && zs && (map.tier ?? 1) >= (zs.minTier ?? 1)) {
      const maxLvl = Math.max(1, ...(this.state.party ?? []).map((h) => h.level ?? 1));
      mobLevel = Math.max(map.levelRange[0], Math.min(maxLvl, map.levelRange[1]));
    }
    this.monsters = (monsters ?? []).map((m, i) => {
      // Everyone stands ON the ground line — a lifted back-row monster reads as
      // floating. Overlap is handled by wider x spacing + depth sort instead.
      const mob = createMonster(this, this.scale.width + 40 + i * 44, this.groundY, m.id, {
        targetHeight: Math.round(88 * (MOB_SIZE[m.id] ?? 1)),
        boss: m.isBoss,
        elite: m.isElite,
        level: mobLevel,
        fallbackTexture: MOB_TEXTURE[m.id],
      });
      mob.container.setData('isMob', true);
      mob.container.setDepth(5 - i); // front of the pack on top, below combat FX (38+)
      mob.id = m.id;
      mob.ranged = RANGED_MONSTERS.has(m.id);
      mob.alive = true;
      mob.setHp(m.hp, m.maxHp);
      // Slide in from the right to its fighting slot — front monster at mobX,
      // the rest staggered behind it (wider gap so silhouettes stay legible).
      const slotX = this.mobX + i * 42;
      this.tweens.add({ targets: mob.container, x: slotX, duration: 200, ease: 'Quad.easeOut' });
      return mob;
    });
    this._advanceHeroes();
  }

  /** First still-living monster in the pack (the focus-fire target). */
  _frontMonster() {
    return this.monsters.find((m) => m && m.alive) ?? null;
  }

  /** Heroes march forward to engage the front of the pack. */
  _advanceHeroes() {
    // Anchor on the front monster's SETTLED slot (mobX), not its live container.x:
    // during the slide-in the monster is still off-screen right, and chasing that
    // would send the heroes past the pack to the screen edge.
    const frontX = this.mobX;
    this.heroes.forEach((hero, i) => {
      if (!this.state.party[i]?.alive) return;
      const gap = (CLASS_ATTACK[hero.classId]?.engageGap ?? 90) + i * 26;
      // Slightly different durations so they don't lunge in perfect lockstep.
      this._heroMoveTo(hero, frontX - gap, hero.homeY, 300 + i * 70);
    });
  }

  /** Heroes walk back to their resting anchors after the pack is cleared. */
  _retreatHeroes() {
    this.heroes.forEach((hero, i) => {
      if (!this.state.party[i]?.alive) return;
      this._heroMoveTo(hero, hero.homeX, hero.homeY, 340);
    });
  }

  /** Walk one hero to a target position (advance to fight / retreat home). */
  _heroMoveTo(hero, x, y, duration) {
    if (!hero) return;
    const c = hero.char.container;
    this.tweens.killTweensOf(c);
    hero.char.walk();
    this.tweens.add({ targets: c, x, y, duration, ease: 'Sine.easeInOut' });
  }

  /** Destroy every monster container (live or mid-death) and kill its tweens. */
  _sweepMonsters() {
    this.children.list
      .filter((o) => o.type === 'Container' && o.getData?.('isMob'))
      .forEach((c) => {
        this.tweens.killTweensOf(c);
        if (c.list) this.tweens.killTweensOf(c.list);
        c.destroy();
      });
    this.monsters = [];
  }

  /**
   * The attack visual for a given hero: the class default, overridden by a
   * learned skill's `visualAttack`. The player's selected `activeSkillVisual`
   * wins; otherwise the last-learned visual skill applies.
   */
  _attackVisual(heroIndex = 0) {
    const classId = this.heroes[heroIndex]?.classId ?? 'knight';
    const base = CLASS_ATTACK[classId] ?? CLASS_ATTACK.knight;
    const hero = this.state?.party?.[heroIndex];
    const learned = hero?.skills ?? [];
    if (!learned.length) return base;
    const classSkills = this.data?.skills?.[classId] ?? [];

    let va = null;
    const active = hero?.activeSkillVisual;
    if (active && learned.includes(active)) {
      va = classSkills.find((s) => s.id === active)?.visualAttack ?? null;
    }
    if (!va) {
      for (const s of classSkills) {
        if (s.visualAttack && learned.includes(s.id)) va = s.visualAttack; // last wins
      }
    }
    if (!va) return base;
    return {
      ...base,
      kind: va.kind ?? base.kind,
      color: va.color != null ? Number(va.color) : base.color,
      scale: va.scale ?? 1,
      fx: va.fx ?? null,
      sprite: va.sprite ?? null,
      travel: va.travel ?? base.travel,
    };
  }

  _onHeroAttack(e) {
    const hero = this.heroes[e.heroIndex];
    if (!hero) return;
    const mob = this.monsters[e.targetIndex];
    if (!mob || !mob.alive) { hero.char.attack(); return; }
    const cfg = this._attackVisual(e.heroIndex);
    const hx = hero.char.container.x;
    const tx = mob.container.x;
    const hy = this.groundY - 42;

    // Damage, hurt-flash and the number only land when the swing/projectile
    // actually reaches the monster — never before.
    const impact = () => {
      mob.hurt?.();
      mob.setHp?.(e.targetHp, e.targetMaxHp);
      this._hitSpark(tx, hy, e.crit, cfg.color);
      if (cfg.fx) this._elementBurst(tx, hy, cfg.fx);
      if (e.crit) {
        this._shake(120, 0.007);
        this._critFlash();
      }
      this._floatText(tx, this.groundY - 78, String(e.damage), e.crit ? '#ffd34d' : '#ffffff', e.crit);
    };

    if (cfg.kind === 'sprite' && cfg.sprite && this.textures.exists(cfg.sprite)) {
      hero.char.attack();
      this._spriteStrike(cfg, tx, hy, impact);
    } else if (cfg.kind === 'melee') {
      // Melee heroes CLOSE THE GAP: dash up to the monster, strike on contact,
      // then fall back to their engage spot — mirror of the melee monsters'
      // bite pattern, so knight/rogue attacks read as real charges instead of
      // a slash landing from a distance. Ranged classes keep firing from afar.
      const c = hero.char.container;
      const fromX = c.x;
      const strikeX = Math.max(fromX, tx - 40); // never dash backwards
      this.tweens.killTweensOf(c);
      hero.char.walk();
      this.tweens.add({
        targets: c, x: strikeX, duration: 150 + (cfg.windup ?? 100), ease: 'Quad.easeIn',
        onComplete: () => {
          hero.char.attack();
          if (mob.alive) this._meleeSlash(tx, hy, cfg.color, cfg.fx);
          impact();
          // Fall back only if the hero survived the round so far — a downed
          // hero's death anim takes over from wherever he fell.
          if (this.state.party[e.heroIndex]?.alive !== false) {
            this.tweens.add({ targets: c, x: fromX, duration: 280, ease: 'Quad.easeOut' });
          }
        },
      });
    } else {
      hero.char.attack();
      this._launchProjectile(cfg, hx + 16, this.groundY - 50, tx - 8, hy, impact);
    }
  }

  _onMonsterAttack(e) {
    const hero = this.heroes[e.heroIndex];
    const mob = this.monsters[e.monsterIndex];
    if (!hero) return;
    const hx = hero.char.container.x;
    const hy = this.groundY - 40;
    const hitHero = () => {
      hero.char.hurt();
      this._hitSpark(hx, hy, false, '#ff8a8a');
      this._floatText(hx, this.groundY - 78, String(e.damage), '#ff8a8a');
    };

    // Ranged monsters strike from their spot — the pose plays, then a small orb
    // travels to the hero so the strike actually reads as an attack (previously
    // this landed instantly with no travelling FX, which read as "nothing
    // happened"). Melee monsters close the gap to the hero (important when the
    // hero is a back-line caster), bite, then fall back.
    if (!mob || !mob.alive) {
      hitHero();
      return;
    }
    if (mob.ranged) {
      mob.attack?.();
      const fx = MONSTER_RANGED_FX[mob.id] ?? MONSTER_RANGED_FX.default;
      const mx = mob.container.x;
      const my = this.groundY - 46;
      this._launchMonsterProjectile(fx, mx - 14, my, hx + 14, hy, hitHero);
      return;
    }

    const c = mob.container;
    const fromX = c.x;
    const targetX = Math.min(c.x - 6, hx + 55);
    this.tweens.killTweensOf(c);
    mob.attack();
    this.tweens.add({
      targets: c, x: targetX, duration: 180, ease: 'Quad.easeIn',
      onComplete: () => {
        hitHero();
        if (mob.alive) this.tweens.add({ targets: c, x: fromX, duration: 240, ease: 'Quad.easeOut' });
      },
    });
  }

  _onKill(e) {
    const mob = this.monsters[e.monsterIndex];
    const mx = mob?.container.x ?? this.mobX;
    if (mob) { mob.die?.(); mob.alive = false; }
    this._deathPoof(mx, this.groundY - 18);
    this._shake(e.isBoss ? 320 : 90, e.isBoss ? 0.012 : 0.004, e.isBoss);
    const msg = e.isBoss ? 'CHEFE DERROTADO!' : `+${e.gold} ⛁`;
    this._floatText(mx, this.groundY - 60, msg, '#e8b454', e.isBoss);
    // Whole pack cleared — heroes walk back to their resting points.
    if (!this._anyMonsterAlive()) this._retreatHeroes();
  }

  // --- Per-hero down / revive feedback --------------------------------------
  _onHeroDown(e) {
    const hero = this.heroes[e.heroIndex];
    if (!hero) return;
    hero.char.hurt();
    this.tweens.killTweensOf(hero.char.container);
    hero.char.container.setAlpha(0.35);
    this._floatText(hero.char.container.x, this.groundY - 86, 'caiu!', '#ff6b6b', true);
  }

  _onHeroRevive(e) {
    const hero = this.heroes[e.heroIndex];
    if (!hero) return;
    hero.char.container.setAlpha(1);
    this._floatText(hero.homeX, this.groundY - 86, 'revivido!', '#7CFC4D', true);
  }

  // --- Level up feedback ----------------------------------------------------
  _onLevelUp(e) {
    const hero = this.heroes[e.heroIndex];
    hero?.char?.sprite?.setTintFill?.(0xffe7a0);
    this.time.delayedCall(120, () => {
      hero?.char?.sprite?.clearTint?.();
      if (hero?.char?.sprite) hero.char.sprite.tintFill = false;
    });
    this._floatText(this._heroX(e.heroIndex), this.groundY - 92, `NÍVEL ${e.level}!`, '#ffd34d', true);
  }

  // --- Combat juice (sparks + dust) -----------------------------------------
  /** Camera shake that won't stack into constant jitter at high kill rate. */
  _shake(duration, intensity, force = false) {
    const cam = this.cameras.main;
    if (!force && cam.shakeEffect?.isRunning) return;
    cam.shake(duration, intensity);
  }

  /** Quick warm screen flash on a crit. Throttled so fast combat never strobes. */
  _critFlash() {
    const now = this.time.now;
    if (now - (this._lastCritFlash ?? 0) < 650) return;
    this._lastCritFlash = now;
    this.cameras.main.flash(110, 255, 236, 190, false);
  }

  /** Expanding shockwave ring — the ground-shaking punctuation of an ultimate. */
  _shockwave(x, y, color = 0xffe9b0, size = 1) {
    const ring = this.add.circle(x, y, 8, color, 0)
      .setStrokeStyle(3, color, 0.9).setDepth(44);
    const ring2 = this.add.circle(x, y, 4, 0xffffff, 0)
      .setStrokeStyle(1.5, 0xffffff, 0.7).setDepth(44);
    this.tweens.add({ targets: ring, scale: 5.5 * size, alpha: 0, duration: 420, ease: 'Quad.easeOut', onComplete: () => ring.destroy() });
    this.tweens.add({ targets: ring2, scale: 4 * size, alpha: 0, duration: 320, delay: 60, ease: 'Quad.easeOut', onComplete: () => ring2.destroy() });
    this._trackFx([ring, ring2]);
  }

  /** Retire the oldest fx objects so bursts never accumulate at high speed. */
  _trackFx(objs) {
    this._fx ??= [];
    this._fx.push(...objs);
    const MAX_FX = 40;
    while (this._fx.length > MAX_FX) {
      const old = this._fx.shift();
      this.tweens.killTweensOf(old);
      old.destroy();
    }
  }

  /** Small spark burst at an impact point. Throttled so rapid hits stay cheap. */
  _hitSpark(x, y, crit = false, color = '#fff3c4') {
    const now = this.time.now;
    if (now - (this._lastSpark ?? 0) < 45) return; // throttle ~22/s
    this._lastSpark = now;
    const tint = typeof color === 'number' ? color : Phaser.Display.Color.HexStringToColor(color).color;
    const n = crit ? 6 : 4;
    const objs = [];
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.5;
      const dist = (crit ? 22 : 14) + Math.random() * 8;
      const dot = this.add.circle(x, y, crit ? 3 : 2, tint, 1).setDepth(40);
      objs.push(dot);
      this.tweens.add({
        targets: dot,
        x: x + Math.cos(a) * dist,
        y: y + Math.sin(a) * dist,
        alpha: 0,
        scale: 0.2,
        duration: crit ? 280 : 200,
        ease: 'Quad.easeOut',
        onComplete: () => dot.destroy(),
      });
    }
    this._trackFx(objs);
  }

  /** Dust puff when a monster dies. */
  _deathPoof(x, y) {
    const now = this.time.now;
    if (now - (this._lastPoof ?? 0) < 60) return;
    this._lastPoof = now;
    const objs = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6 + Math.random() * 0.6;
      const dist = 16 + Math.random() * 14;
      const shade = [0xcdbb96, 0xa8916b, 0x8d7a58][i % 3];
      const puff = this.add.circle(x, y, 3 + Math.random() * 2, shade, 0.85).setDepth(38);
      objs.push(puff);
      this.tweens.add({
        targets: puff,
        x: x + Math.cos(a) * dist,
        y: y - 6 - Math.random() * 14,
        alpha: 0,
        scale: 1.6,
        duration: 360,
        ease: 'Quad.easeOut',
        onComplete: () => puff.destroy(),
      });
    }
    this._trackFx(objs);
  }

  // --- Per-class attack effects (projectiles + slashes) ---------------------
  /** Build the visual for a flying attack, anchored at the launch point. */
  _makeProjectile(cfg, x, y) {
    const c = this.add.container(x, y).setDepth(42);
    if (cfg.kind === 'fireball') {
      c.add(this.add.circle(0, 0, 8, 0xff4500, 0.4));   // outer glow
      c.add(this.add.circle(0, 0, 5, cfg.color, 1));     // core
      c.add(this.add.circle(-1, -1, 2, 0xffe9a0, 1));    // hot center
    } else if (cfg.kind === 'arrow') {
      const shaft = this.add.rectangle(0, 0, 16, 2, cfg.color).setOrigin(0.5);
      const head = this.add.triangle(8, 0, 0, -3, 0, 3, 5, 0, 0xcfcfcf);
      const fletch = this.add.rectangle(-7, 0, 3, 5, 0xb05a3a).setOrigin(0.5);
      c.add([shaft, head, fletch]);
    } else { // bolt (holy)
      c.add(this.add.circle(0, 0, 7, 0xfff4cf, 0.35));
      c.add(this.add.circle(0, 0, 4, cfg.color, 1));
      c.add(this.add.circle(0, 0, 2, 0xffffff, 1));
    }
    return c;
  }

  /** Fire a projectile from hero to monster; `onHit` runs on arrival. */
  _launchProjectile(cfg, x0, y0, x1, y1, onHit) {
    const proj = this._makeProjectile(cfg, x0, y0);
    if (cfg.scale && cfg.scale !== 1) proj.setScale(cfg.scale);
    if (cfg.kind === 'arrow') proj.setRotation(Math.atan2(y1 - y0, x1 - x0));
    // A small flash at the hero hand as it leaves.
    this._hitSpark(x0, y0, false, cfg.color);
    const trail = cfg.kind === 'fireball' || cfg.kind === 'bolt';
    this.tweens.add({
      targets: proj,
      x: x1, y: y1,
      duration: cfg.travel,
      ease: 'Sine.easeIn',
      onUpdate: trail ? () => this._projTrail(proj.x, proj.y, cfg.color) : undefined,
      onComplete: () => { proj.destroy(); onHit(); },
    });
    this._trackFx([proj]);
  }

  /** Fire a small glowing orb from a ranged monster to the hero; `onHit` runs on arrival. */
  _launchMonsterProjectile(fx, x0, y0, x1, y1, onHit) {
    const c = this.add.container(x0, y0).setDepth(42);
    c.add(this.add.circle(0, 0, 7, fx.glow, 0.4));
    c.add(this.add.circle(0, 0, 4, fx.color, 1));
    c.add(this.add.circle(-1, -1, 1.5, 0xffffff, 0.8));
    this._hitSpark(x0, y0, false, fx.color);
    this.tweens.add({
      targets: c,
      x: x1, y: y1,
      duration: fx.travel,
      ease: 'Sine.easeIn',
      onUpdate: () => this._projTrail(c.x, c.y, fx.color),
      onComplete: () => { c.destroy(); onHit(); },
    });
    this._trackFx([c]);
  }

  /** Faint fading trail behind a magic projectile. */
  _projTrail(x, y, color) {
    if (this.time.now - (this._lastTrail ?? 0) < 30) return;
    this._lastTrail = this.time.now;
    const dot = this.add.circle(x, y, 3, color, 0.6).setDepth(41);
    this.tweens.add({ targets: dot, alpha: 0, scale: 0.3, duration: 220, onComplete: () => dot.destroy() });
    this._trackFx([dot]);
  }

  /** A quick slash arc flashing on the monster for melee classes. */
  _meleeSlash(x, y, color, fx = null) {
    const w = fx ? 38 : 30;
    const slash = this.add.rectangle(x, y, w, fx ? 6 : 5, color, 0.95)
      .setOrigin(0.5).setRotation(-0.7).setDepth(42);
    this.tweens.add({
      targets: slash, rotation: 0.5, scaleX: 1.4, alpha: 0,
      duration: 160, ease: 'Quad.easeOut', onComplete: () => slash.destroy(),
    });
    this._trackFx([slash]);
  }

  /** A signature-magic sprite that strikes down onto the monster, then impacts. */
  _spriteStrike(cfg, x, y, onHit) {
    const tex = this.textures.get(cfg.sprite).getSourceImage();
    // ~92px tall baseline, boosted by the skill's own scale (ultimates run big).
    const s = (92 * (cfg.scale ?? 1)) / tex.height;
    const big = (cfg.scale ?? 1) >= 1.15; // ultimate-grade: extra punctuation
    // Bottom-anchored just under the monster's centre so the effect reads as a
    // strike landing ON it; it stretches in from the top (snap-down) then fades.
    const spr = this.add.sprite(x, y + 16, cfg.sprite)
      .setOrigin(0.5, 1).setDepth(45).setScale(s, s * 0.55).setAlpha(0);
    this.tweens.add({
      targets: spr, alpha: 1, scaleY: s, duration: 90, ease: 'Back.easeOut',
      onComplete: () => {
        this._shake(big ? 220 : 140, big ? 0.011 : 0.008, big);
        this._shockwave(x, y + 12, cfg.color ?? 0xffe9b0, big ? 1.4 : 0.9);
        if (big) {
          this.cameras.main.flash(90, 255, 244, 210, false);
          bus.emit('fx:ultimate'); // signature-move sound (ui/sfx.js)
        }
        onHit(); // damage + hurt + spark + element burst + number
        this.tweens.add({
          targets: spr, alpha: 0, y: spr.y - 8,
          duration: 200, delay: big ? 140 : 70, ease: 'Quad.easeIn',
          onComplete: () => spr.destroy(),
        });
      },
    });
    this._trackFx([spr]);
  }

  /**
   * Elemental flourish on impact for skill-empowered attacks — fire flames rise,
   * holy light sparkles in a halo, ice shards scatter. Pooled + throttled.
   */
  _elementBurst(x, y, fx) {
    const now = this.time.now;
    if (now - (this._lastBurst ?? 0) < 50) return;
    this._lastBurst = now;
    const PALETTE = {
      fire:   [0xff3010, 0xff7a2a, 0xffd34d],
      holy:   [0xfff0b0, 0xffe08a, 0xffffff],
      ice:    [0x9fe8ff, 0x5bb0e0, 0xffffff],
      poison: [0x4caf2a, 0x7CFC4D, 0xd6ff8a],
      shadow: [0x6a2fae, 0xb56cff, 0x3a1f5c],
      power:  [0xdfe8f5, 0xffffff, 0xa8c0d8],
      bluefire: [0x1a6bff, 0x4dc8ff, 0xc8f0ff], // hotter blue flame (pyromancer)
      // Ultimate-tier flavours (tier-6 signature strikes).
      void:      [0x2a1040, 0x7a3bd0, 0xc9a0ff], // rogue — reality tear
      meteor:    [0xff5a1c, 0x8a4a2a, 0xffd34d], // ranger — burning rock
      supernova: [0xffffff, 0xffd34d, 0xff5a1c], // pyromancer — stellar blast
      seraph:    [0xfff8e0, 0xffe08a, 0xffffff], // priest — angelic light
    };
    const colors = PALETTE[fx] ?? PALETTE.fire;
    const objs = [];
    const n = 8;
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.6;
      const dist = 14 + Math.random() * 16;
      const col = colors[i % colors.length];
      const p = this.add.circle(x, y, 2 + Math.random() * 2, col, 1).setDepth(43);
      objs.push(p);
      // Fire/holy rise upward; ice scatters flatter.
      const lift = fx === 'ice' ? Math.sin(a) * dist : -10 - Math.random() * 18;
      this.tweens.add({
        targets: p,
        x: x + Math.cos(a) * dist,
        y: y + lift,
        alpha: 0,
        scale: fx === 'holy' ? 1.8 : 0.3,
        duration: 300 + Math.random() * 120,
        ease: 'Quad.easeOut',
        onComplete: () => p.destroy(),
      });
    }
    // A quick expanding ring for extra pop.
    const ring = this.add.circle(x, y, 6, colors[0], 0).setStrokeStyle(2, colors[1], 0.9).setDepth(43);
    objs.push(ring);
    this.tweens.add({ targets: ring, scale: 3, alpha: 0, duration: 260, ease: 'Quad.easeOut', onComplete: () => ring.destroy() });
    this._trackFx(objs);
  }

  // --- Drop toast -----------------------------------------------------------
  _dropToast(label, color, big = false) {
    const { width } = this.scale;
    const t = this.add
      .text(width / 2, this.groundY + 8, `✦ ${label}`, {
        fontFamily: 'monospace', fontSize: big ? '12px' : '10px', color,
        stroke: '#000000', strokeThickness: 3,
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0);
    this.tweens.add({ targets: t, alpha: 1, y: this.groundY - 2, duration: 180, yoyo: true, hold: 900, onComplete: () => t.destroy() });
  }

  // --- Drop balloon (clickable, above the hero) -----------------------------
  /**
   * An item-card pops over the hero who got the drop — styled like the
   * "<item> obtained" tooltip in the reference art: a dark card with a thin
   * rarity-coloured frame, a header band carrying the item name (in its rarity
   * colour) over a hairline divider, then the item glyph + "obtido" subtitle,
   * and a little tail pointing down at the hero. Clicking it opens the HERO
   * window (backpack). It floats up and auto-fades; concurrent cards are capped.
   */
  _dropBalloon(heroIndex, { glyph, label, color }) {
    this._balloons ??= [];
    while (this._balloons.length >= 3) {
      const old = this._balloons.shift();
      this.tweens.killTweensOf(old);
      old.destroy();
    }

    const tint = typeof color === 'number' ? color : Phaser.Display.Color.HexStringToColor(color).color;
    const nameHex = typeof color === 'string' ? color : '#ffd98a';

    const c = this.add.container(this._heroX(heroIndex), this.groundY - 116).setDepth(60);
    c.setData('heroIndex', heroIndex); // so it can track the hero in update()

    // Name first so we can size the card to it.
    const name = this.add
      .text(0, 0, label, {
        fontFamily: 'monospace', fontSize: '9px', fontStyle: 'bold',
        color: nameHex, stroke: '#000000', strokeThickness: 2,
      })
      .setOrigin(0.5);
    const W = Math.max(64, Math.ceil(name.width) + 30);
    c.setData('cardW', W); // for on-screen clamping while it follows the hero
    const HEAD = 15, BODY = 19, H = HEAD + BODY;
    // Keep the whole card on-screen even when the hero is near an edge.
    c.x = Phaser.Math.Clamp(c.x, W / 2 + 4, this.scale.width - W / 2 - 4);

    const g = this.add.graphics();
    // Drop shadow.
    g.fillStyle(0x000000, 0.4);
    g.fillRoundedRect(-W / 2 + 2, -H / 2 + 3, W, H, 5);
    // Card body (dark) + rarity frame.
    g.fillStyle(0x16130f, 0.97);
    g.fillRoundedRect(-W / 2, -H / 2, W, H, 5);
    // Header band (rarity tint, dim) behind the name.
    g.fillStyle(tint, 0.16);
    g.fillRect(-W / 2 + 2, -H / 2 + 2, W - 4, HEAD - 2);
    // Hairline divider under the header.
    g.lineStyle(1, tint, 0.55);
    g.lineBetween(-W / 2 + 4, -H / 2 + HEAD, W / 2 - 4, -H / 2 + HEAD);
    // Outer frame + a soft inner gold edge for the "ornate" feel.
    g.lineStyle(2, tint, 1);
    g.strokeRoundedRect(-W / 2, -H / 2, W, H, 5);
    g.lineStyle(1, 0xffe9b0, 0.18);
    g.strokeRoundedRect(-W / 2 + 2, -H / 2 + 2, W - 4, H - 4, 4);
    // Tail pointing down at the hero.
    g.fillStyle(0x16130f, 0.97);
    g.fillTriangle(-5, H / 2 - 1, 5, H / 2 - 1, 0, H / 2 + 7);
    g.lineStyle(2, tint, 1);
    g.lineBetween(-5, H / 2 - 1, 0, H / 2 + 7);
    g.lineBetween(5, H / 2 - 1, 0, H / 2 + 7);

    name.setPosition(0, -H / 2 + HEAD / 2 + 1);
    const ico = this.add.text(-W / 2 + 12, -H / 2 + HEAD + BODY / 2, glyph, { fontSize: '13px' }).setOrigin(0.5);
    const sub = this.add
      .text(-W / 2 + 22, -H / 2 + HEAD + BODY / 2, 'obtido — abrir', {
        fontFamily: 'monospace', fontSize: '8px', color: '#cdbfa6',
      })
      .setOrigin(0, 0.5);
    c.add([g, name, ico, sub]);

    const retire = () => {
      const i = this._balloons.indexOf(c);
      if (i !== -1) this._balloons.splice(i, 1);
      this.tweens.killTweensOf(c);
      c.destroy();
    };

    // Clickable card → open the backpack (HERO window).
    c.setInteractive(new Phaser.Geom.Rectangle(-W / 2, -H / 2, W, H + 10), Phaser.Geom.Rectangle.Contains);
    c.on('pointerover', () => { this.input.setDefaultCursor('pointer'); c.setScale(1.06); });
    c.on('pointerout', () => { this.input.setDefaultCursor('default'); c.setScale(1); });
    c.on('pointerdown', () => { this.input.setDefaultCursor('default'); bus.emit('ui:openHero', { heroIndex }); retire(); });

    const baseY = c.y;
    c.setScale(0.2);
    this.tweens.add({ targets: c, scale: 1, duration: 170, ease: 'Back.easeOut' });
    this.tweens.add({ targets: c, y: baseY - 16, duration: 3800, ease: 'Sine.easeOut' });
    this.tweens.add({ targets: c, alpha: 0, delay: 3200, duration: 600, ease: 'Quad.easeIn', onComplete: retire });
    this._balloons.push(c);
  }

  // --- Floating combat text -------------------------------------------------
  _floatText(x, y, text, color, big = false) {
    // At high combat speed (Nv.90+) dozens of damage numbers spawn per second
    // and pile into an unreadable garbled column. Cap how many float at once:
    // when over the limit, retire the oldest immediately so new hits stay legible.
    this._floaters ??= [];
    const MAX_FLOATERS = big ? 12 : 6;
    while (this._floaters.length >= MAX_FLOATERS) {
      const old = this._floaters.shift();
      this.tweens.killTweensOf(old);
      old.destroy();
    }

    const t = this.add
      .text(x, y, text, {
        fontFamily: 'monospace', fontSize: big ? '16px' : '12px', color,
        stroke: '#000000', strokeThickness: 3,
      })
      .setOrigin(0.5, 1);
    this._floaters.push(t);
    this.tweens.add({
      targets: t, y: y - 22, alpha: 0, duration: 700, ease: 'Quad.easeOut',
      onComplete: () => {
        t.destroy();
        const i = this._floaters.indexOf(t);
        if (i !== -1) this._floaters.splice(i, 1);
      },
    });
  }
}
